import { useMemo, useState } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { Hook, rossumStore } from '@rossum/api-client/hooks';
import { ListResponse } from '@rossum/api-client/utils';
import {
  IconAlertOctagon,
  IconLibrary,
  IconLogs,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Stack,
  SvgIcon,
  Switch,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { activitiesExtensionsPath } from '../../../../../containers/Activities/routes';
import { linebreak } from '../../../../../lib/formaterValues';
import { WorkspaceWithQueues } from '../../../../../types/workspace';
import { DeleteExtensionDialog } from '../../../components/DeleteExtensionDialog';
import { DuplicateExtensionDialog } from '../../../components/DuplicateExtensionDialog';
import { QUERY_KEY_HOOKS } from '../../../hooks/useHooks';
import { usePatchHook } from '../../../hooks/usePatchHook';
import { getIcon } from '../../../lib/helpers';
import { extensionDetailPath } from '../../../routes';
import { EmptyExtensionsList } from '../components/EmptyExtensionsList';
import { EventsBadges } from './components/EventsBadges';
import { ExpandableWorkspaceBadges } from './components/ExpandableWorkspaceBadges';
import { FirstExtensionBanner } from './components/FirstExtensionBanner';
import { StoreAdBanner } from './components/StoreAdBanner';

const actionButtonsClassName = 'extension-action-buttons';

const clearLinkStyles: SxProps<Theme> = {
  color: 'unset',
  '&:hover': {
    color: 'unset',
    textDecoration: 'none',
  },
};
type Props = {
  extensions: Array<Hook>;
  extensionsLoaded: boolean;
  filtersActive: boolean;
  workspacesWithQueues?: WorkspaceWithQueues[];
  hookQuery: unknown;
};

export const ExtensionsList = ({
  extensions,
  extensionsLoaded,
  filtersActive,
  workspacesWithQueues,
  hookQuery,
}: Props) => {
  const intl = useIntl();

  const [contextExtension, setContextExtension] = useState<
    { id: number; action: 'delete' | 'duplicate' } | undefined
  >(undefined);

  const queryClient = useQueryClient();
  const { mutate } = usePatchHook();

  const debouncedUpdate = useMemo(
    () => R.debounce(mutate, { waitMs: 300 }),
    [mutate]
  );

  if (extensions.length === 0 && extensionsLoaded) {
    return filtersActive ? (
      <EmptyExtensionsList />
    ) : (
      <>
        <StoreAdBanner />
        <FirstExtensionBanner dataCy="extensions-intro-create-banner" />
      </>
    );
  }

  const contextExtensionHook = extensions.find(
    extension => extension.id === contextExtension?.id
  );

  return (
    <Stack spacing={3}>
      <List disablePadding sx={{ whiteSpace: 'nowrap' }}>
        {extensions.map(extension => {
          return (
            <ListItem
              key={extension.id}
              disablePadding
              sx={{
                px: 1,
                '&:hover, &:focus-within': {
                  backgroundColor: 'background.paper',
                  [`& .${actionButtonsClassName}`]: {
                    visibility: 'visible',
                  },
                },
              }}
              alignItems="center"
              data-cy="extensions-table-row"
            >
              <Stack minWidth={50}>
                <Tooltip
                  placement="top"
                  disableInteractive
                  title={intl.formatMessage(
                    {
                      id: 'containers.settings.extensions.activityToggle',
                    },
                    { linebreak }
                  )}
                >
                  <Stack>
                    <Switch
                      disabled={
                        extension.type === 'function' &&
                        (extension.status === 'failed' ||
                          extension.status === 'pending')
                      }
                      checked={extension.active}
                      onChange={() => {
                        // Optimistic update to have UI more responsive.
                        queryClient.setQueryData(
                          [QUERY_KEY_HOOKS, 'list', hookQuery],
                          (state: ListResponse<Hook> | undefined) =>
                            produce(state, ext => {
                              const updated = ext?.results.find(
                                item => item.id === extension.id
                              );
                              if (updated) {
                                updated.active = !extension.active;
                              }
                            })
                        );

                        debouncedUpdate.call({
                          hookId: extension.id,
                          payload: {
                            active: !extension.active,
                            type: extension.type,
                          },
                        });
                      }}
                      data-cy="extensions-list-toggle"
                    />
                  </Stack>
                </Tooltip>
              </Stack>
              <Stack
                direction="row"
                flex={1}
                alignItems="center"
                overflow="hidden"
              >
                <Stack
                  direction="row"
                  component={RouterLink}
                  to={extensionDetailPath(extension.id)}
                  alignItems="center"
                  sx={{
                    ...clearLinkStyles,
                    minHeight: 60,
                    // to make outline visible we add margin here, otherwise it gets hidden by overflow
                    m: '3px',
                  }}
                  flex="0 1 90%"
                  overflow="hidden"
                >
                  <Stack
                    flex="0 1 60%"
                    overflow="hidden"
                    // to enable text truncation based on flex width we must define a minWidth recursively to the typogrophy child
                    minWidth={0}
                    spacing={1.5}
                    direction="row"
                    sx={{ svg: { display: 'block' } }}
                    alignItems="center"
                  >
                    {extension.type === 'function' &&
                    extension.status === 'pending' ? (
                      <Tooltip
                        placement="top"
                        disableInteractive
                        title={intl.formatMessage({
                          id: `components.editor.infoMessage.pending.${extension.type}`,
                        })}
                      >
                        <CircularProgress size={20} color="inherit" />
                      </Tooltip>
                    ) : (
                      <Box sx={{ color: 'text.secondary' }}>
                        {getIcon(
                          extension.type,
                          get(extension.config, 'runtime'),
                          { fontSize: 'large' },
                          { disableInteractive: true }
                        )}
                      </Box>
                    )}
                    {extension.type === 'function' &&
                      extension.status === 'failed' && (
                        <Tooltip
                          placement="top"
                          disableInteractive
                          title={intl.formatMessage({
                            id: 'containers.settings.extensions.retryTooltip',
                          })}
                        >
                          <SvgIcon color="error" fontSize="medium">
                            <IconAlertOctagon />
                          </SvgIcon>
                        </Tooltip>
                      )}
                    <Stack flex={1} minWidth={0} maxWidth={600}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          data-cy="extensions-list-name"
                          variant="body2"
                          fontWeight="bold"
                        >
                          {extension.name}
                        </Typography>
                        {extension.extensionSource === rossumStore && (
                          <Chip
                            label={intl.formatMessage({
                              id: 'containers.settings.extensions.title.fromStore.tag',
                            })}
                          />
                        )}
                      </Stack>

                      {extension.description && (
                        <Typography
                          variant="body2"
                          overflow="hidden"
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                          sx={{ color: 'text.secondary', flex: 1 }}
                        >
                          {extension.description}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                  <Stack flex="0 0 20%" overflow="hidden">
                    <EventsBadges id={extension.id} events={extension.events} />
                  </Stack>
                  <Stack flex="0 0 20%" overflow="hidden">
                    {workspacesWithQueues !== undefined ? (
                      extension.queues?.length > 0 ? (
                        <ExpandableWorkspaceBadges
                          id="expandable-ws-qs-badges-extensions-list"
                          queueUrls={extension.queues}
                          workspaces={workspacesWithQueues}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{ ml: 1 }}
                        >
                          {intl.formatMessage(
                            {
                              id: 'containers.settings.extensions.queues',
                            },
                            { count: extension.queues.length }
                          )}
                        </Typography>
                      )
                    ) : (
                      <Skeleton width={60} height="100%" />
                    )}
                  </Stack>
                </Stack>
                <Stack
                  flex="1 0 10%"
                  direction="row"
                  spacing={1}
                  flexDirection="row-reverse"
                  className={actionButtonsClassName}
                  sx={{
                    visibility: 'hidden',
                  }}
                >
                  <Tooltip
                    placement="top"
                    disableInteractive
                    title={intl.formatMessage({
                      id: 'containers.settings.extensions.actionButtons.duplicate',
                    })}
                  >
                    <IconButton
                      onClick={() =>
                        setContextExtension({
                          id: extension.id,
                          action: 'duplicate',
                        })
                      }
                    >
                      <SvgIcon fontSize="large">
                        <IconLibrary />
                      </SvgIcon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    placement="top"
                    disableInteractive
                    title={intl.formatMessage({
                      id: 'containers.settings.extensions.actionButtons.delete',
                    })}
                  >
                    <IconButton
                      onClick={() =>
                        setContextExtension({
                          id: extension.id,
                          action: 'delete',
                        })
                      }
                    >
                      <SvgIcon fontSize="large">
                        <IconTrash />
                      </SvgIcon>
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    placement="top"
                    disableInteractive
                    title={intl.formatMessage({
                      id: 'containers.settings.extensions.actionButtons.activities',
                    })}
                  >
                    <IconButton
                      component={RouterLink}
                      to={`${activitiesExtensionsPath()}?hook=${extension.id}`}
                    >
                      <SvgIcon fontSize="large">
                        <IconLogs />
                      </SvgIcon>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </ListItem>
          );
        })}
      </List>

      {extensionsLoaded && <StoreAdBanner withBackground={false} />}

      <DeleteExtensionDialog
        open={contextExtension?.action === 'delete'}
        onClose={() => setContextExtension(undefined)}
        extension={contextExtensionHook}
      />
      <DuplicateExtensionDialog
        open={contextExtension?.action === 'duplicate'}
        onClose={() => setContextExtension(undefined)}
        extension={contextExtensionHook}
      />
    </Stack>
  );
};
